import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '@/components/mafia/DashboardLayout';
import { MafiaMemberCard } from '@/components/mafia/MafiaMemberCard';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

export default function DashboardCardPage() {
  const navigate = useNavigate();
  const { gamer, loading } = useMafiaAuth();

  useEffect(() => {
    if (!loading && !gamer) navigate('/mafia/login', { replace: true });
  }, [loading, gamer, navigate]);

  const profileUrl = useMemo(
    () => (gamer ? `${window.location.origin}/p/${gamer.member_no}` : ''),
    [gamer]
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      toast.success('Profile link copied');
    } catch { toast.error('Could not copy'); }
  };

  if (loading || !gamer) {
    return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;
  }

  return (
    <DashboardLayout title="Membership Card" subtitle="Your Mafia Salon ID">
      <div className="max-w-2xl mx-auto" data-testid="dashboard-card-page">
        <MafiaMemberCard user={gamer} profileUrl={profileUrl} />

        <section className="ms-card mt-10">
          <div className="px-5 py-4 ms-border-b">
            <p className="ms-kicker">Card details</p>
          </div>
          <div className="ms-divide">
            <Row k="Member ID" v={gamer.member_no} />
            <Row k="Rank" v={gamer.rank} accent />
            <Row k="Points" v={gamer.points} />
            <Row k="Games played" v={gamer.games_played} />
            <Row k="Wins" v={gamer.wins} />
            <Row k="Joined" v={gamer.joined_at ? new Date(gamer.joined_at).toLocaleDateString() : '—'} />
            <div className="flex items-center justify-between px-5 py-3 gap-4">
              <span className="text-xs ms-text-2 shrink-0">Public profile</span>
              <div className="flex items-center gap-2 min-w-0">
                <a
                  href={`/p/${gamer.member_no}`}
                  data-testid="card-public-link"
                  className="text-xs ms-text-red hover:underline truncate"
                >
                  {profileUrl}
                </a>
                <button onClick={copyLink} className="ms-btn ms-btn-ghost ms-btn-sm shrink-0" data-testid="card-copy-link" title="Copy link">
                  <Copy size={12} />
                </button>
                <button onClick={() => window.open(`/p/${gamer.member_no}`, '_blank')} className="ms-btn ms-btn-ghost ms-btn-sm shrink-0" title="Open">
                  <ExternalLink size={12} />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

function Row({ k, v, accent }) {
  return (
    <div className="flex items-center justify-between px-5 py-3 gap-4">
      <span className="text-xs ms-text-2 shrink-0">{k}</span>
      <span className={`text-sm tabular-nums text-right ${accent ? 'ms-text-red font-semibold' : 'ms-text'}`}>{v}</span>
    </div>
  );
}
